import React, { useEffect, useRef, useState } from 'react';
import { Slider as AntSlider } from 'antd';
import type { SliderSingleProps } from 'antd';
import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import css from './DailyCapField.module.scss';

/**
 * antd forwards every prop it does not consume straight to rc-slider
 * (antd/lib/slider/index.js spreads `restProps`), but its own types omit
 * rc-slider's two a11y props. Without them the handle ships with no accessible
 * name. Widen the type rather than casting at the call site, so the escape
 * hatch is declared once and does not read as an accident.
 */
const Slider = AntSlider as React.FC<
  SliderSingleProps & {
    ariaLabelForHandle?: string;
    ariaValueTextFormatterForHandle?: (value: number) => string;
  }
>;

// rc-slider fires a change-complete on every arrow keypress, so a keyboard user
// walking 10 -> 15 produces five of them. Without this they would race each
// other and the slider would snap back to whichever landed first. Long enough
// to absorb a run of keypresses, short enough to feel immediate.
const COMMIT_DELAY_MS = 450;

interface DailyCapFieldProps {
  /** The user's effective cap. Null while the settings GET is unresolved. */
  value: number | null;
  /** Bounds from the API — never hardcode 5/15, so backend tuning moves the UI. */
  min: number;
  max: number;
  disabled?: boolean;
  /**
   * Display only — unlike RadioCardGroup's identically-named prop this does NOT
   * disable the control. Commits are debounced, so a save in flight means the
   * user has paused, not that they are done; locking them out mid-adjustment
   * would be the wrong trade.
   */
  saving?: boolean;
  /** Per-consumer spacing — the fieldset itself carries no margins. */
  className?: string;
  /**
   * Fired ONLY when the user commits a value different from the stored one.
   * Reject (or return a rejected promise) to roll the handle back — the caller
   * owns the error message, this owns the handle.
   */
  onCommit: (value: number) => void | Promise<unknown>;
}

/**
 * "New matches per day" — the user's own daily opportunity budget.
 *
 * Two things this control has to get right:
 *
 *  1. It must not save unless the user genuinely chose. The stored column is
 *     nullable, and null means "follow the global default", so writing the
 *     current value back pins the user to today's number forever and opts them
 *     out of any later retune. Dragging and releasing on the original value
 *     therefore commits nothing.
 *  2. The number is best-effort, not a ceiling. A first run reveals at least a
 *     small floor even when the day's budget is spent, so someone who creates
 *     several searches in one day genuinely exceeds this. The copy says "about"
 *     and names that case rather than pretending it can't happen.
 */
const DailyCapField: React.FC<DailyCapFieldProps> = ({
  value,
  min,
  max,
  disabled,
  saving,
  className,
  onCommit,
}) => {
  // Local, so dragging is smooth and a released-back-where-it-started drag can
  // be detected and skipped.
  const [draft, setDraft] = useState<number>(value ?? min);
  const pendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read at FIRE time, not schedule time, so a debounced commit compares
  // against what is stored now rather than what was stored when it was queued.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  // Re-seed from the stored value — but never while the user's own change is
  // still queued, or the slider would jump back under their fingers mid-run.
  useEffect(() => {
    if (value != null && pendingRef.current === null) setDraft(value);
  }, [value]);

  useEffect(
    () => () => {
      if (pendingRef.current) clearTimeout(pendingRef.current);
    },
    []
  );

  const unavailable = value == null;
  const locked = unavailable || disabled;

  const handleComplete = (next: number) => {
    // Before the GET resolves, `draft` is the placeholder minimum and there is
    // nothing to compare against — committing here would write that minimum
    // over whatever the user actually has stored. `disabled` alone is not
    // enough: rc-slider still runs its keyboard handlers.
    if (valueRef.current == null) return;

    if (pendingRef.current) clearTimeout(pendingRef.current);
    pendingRef.current = setTimeout(() => {
      pendingRef.current = null;
      // No-op saves are the other hazard — see the note above. Checked here so
      // a run of keypresses that ends where it started writes nothing at all.
      if (next === valueRef.current) return;
      // Roll the handle back if the save fails. The stored value is unchanged
      // on failure, so the re-seed effect never fires and the slider would sit
      // on a number the server never accepted. The caller shows the message.
      Promise.resolve(onCommitRef.current(next)).catch(() => {
        setDraft(valueRef.current ?? min);
      });
    }, COMMIT_DELAY_MS);
  };

  return (
    <fieldset className={`${css.fieldset} ${className ?? ''}`} disabled={locked}>
      <legend className={css.legend}>
        {/* Decorative: the legend text already names the group, so letting the
            icon into the accessible name would only add "inbox" to it. */}
        <InboxOutlined className={css.legendIcon} aria-hidden="true" />
        New matches per day
        {saving && <LoadingOutlined className={css.savingIcon} aria-hidden="true" />}
      </legend>

      <p className={css.helper}>
        How many new matches we aim to show you each day, across all your
        searches.
      </p>

      <div className={css.readout}>
        <span className={css.number}>{unavailable ? '—' : draft}</span>
        <span className={css.unit}>a day</span>
      </div>

      <div className={css.sliderRow}>
        <span className={css.bound} aria-hidden="true">
          {min}
        </span>
        <Slider
          className={css.slider}
          min={min}
          max={max}
          step={1}
          value={draft}
          disabled={locked}
          onChange={setDraft}
          onChangeComplete={handleComplete}
          // The readout above already states the value, so a tooltip that
          // repeats it under the cursor is noise.
          tooltip={{ open: false }}
          ariaLabelForHandle="New matches per day"
          // Bare "10" tells a screen-reader user nothing about the unit.
          ariaValueTextFormatterForHandle={(v) => `${v} a day`}
        />
        <span className={css.bound} aria-hidden="true">
          {max}
        </span>
      </div>

      <p className={css.footnote}>
        It's a target, not a hard limit — starting a new search can add a few
        extra that day.
      </p>
    </fieldset>
  );
};

export default DailyCapField;
